/**
 * The route readout: summary, alternatives, legs, turn-by-turn, elevation.
 *
 * Directions used to be collapsed by default in a panel that also held the
 * editing controls, which meant the most-consulted part of the tool was two
 * clicks away and vanished whenever a waypoint moved. Here they are the default
 * content of their own tab and survive a recalculation.
 */
import React, { useState } from 'react';
import { Box, Collapse, Tooltip, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import FlagIcon from '@mui/icons-material/Flag';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import RoundaboutLeftIcon from '@mui/icons-material/RoundaboutLeft';
import TurnLeftIcon from '@mui/icons-material/TurnLeft';
import TurnRightIcon from '@mui/icons-material/TurnRight';
import TurnSlightLeftIcon from '@mui/icons-material/TurnSlightLeft';
import TurnSlightRightIcon from '@mui/icons-material/TurnSlightRight';
import UTurnLeftIcon from '@mui/icons-material/UTurnLeft';
import { DistanceUnits, ManeuverStep } from '../types';
import { formatArrival, formatDelta, formatDistance, formatDuration } from '../geo/format';
import ElevationProfile from '../panel/ElevationProfile';
import { DirectionsIntel, IntelSection } from './intel';
import { EmptyState, GOLD, Loading, Provenance, TabHeader } from './DockPrimitives';
import { DockTabHelp } from '../../missions/DockTabHelp';

/** Distinct from the primary's blue and from every threat colour on the map. */
export const ALTERNATE_COLOR = '#00b8a9';

function maneuverIcon(step: ManeuverStep) {
  const sx = { fontSize: 14, color: 'text.secondary' } as const;
  if (step.type === 'arrive') return <FlagIcon sx={sx} />;
  if (step.type === 'roundabout' || step.type === 'rotary') return <RoundaboutLeftIcon sx={sx} />;
  if (step.type === 'merge') return <MergeTypeIcon sx={sx} />;
  switch (step.modifier) {
    case 'left': return <TurnLeftIcon sx={sx} />;
    case 'right': return <TurnRightIcon sx={sx} />;
    case 'slight left': return <TurnSlightLeftIcon sx={sx} />;
    case 'slight right': return <TurnSlightRightIcon sx={sx} />;
    case 'sharp left': return <TurnLeftIcon sx={sx} />;
    case 'sharp right': return <TurnRightIcon sx={sx} />;
    case 'uturn': return <UTurnLeftIcon sx={sx} />;
    case 'straight': return <ArrowRightAltIcon sx={sx} />;
    default: return <ArrowForwardIcon sx={sx} />;
  }
}

const SubHeader: React.FC<{
  label: string;
  hint?: string;
  open: boolean;
  onToggle: () => void;
}> = ({ label, hint, open, onToggle }) => (
  <Box
    onClick={onToggle}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => { if (e.key === 'Enter') onToggle(); }}
    sx={{
      display: 'flex', alignItems: 'baseline', gap: 0.75, cursor: 'pointer', py: 0.5,
      '&:focus-visible': { outline: `1px solid ${GOLD}`, outlineOffset: 2 },
    }}
  >
    <Typography variant="caption" sx={{ fontWeight: 700 }}>{label}</Typography>
    {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    <Box sx={{ flex: 1 }} />
    <Typography variant="caption" sx={{ color: GOLD, fontWeight: 700, fontSize: '0.64rem' }}>
      {open ? 'Hide' : 'Show'}
    </Typography>
  </Box>
);

export interface DirectionsTabProps {
  section: IntelSection<DirectionsIntel>;
  units: DistanceUnits;
  /** Absent when the route is not editable, e.g. viewing a saved mission. */
  onSelectRoute?: (index: number) => void;
  /** Keeps one of the offered routes as the mission's pre-vetted fallback. */
  onSetAlternate?: (index: number) => void;
  alternateIndex?: number | null;
  primaryName?: string;
  alternateName?: string;
  onHoverRoute?: (index: number | null) => void;
  onFlyToStep?: (step: ManeuverStep) => void;
  onHoverElevation?: (point: { longitude: number; latitude: number } | null) => void;
  /** Live geometry for the elevation chart; not stored with the directions. */
  geometry?: GeoJSON.LineString | null;
}

const DirectionsTab: React.FC<DirectionsTabProps> = ({
  section,
  units,
  onSelectRoute,
  onSetAlternate,
  alternateIndex,
  primaryName,
  alternateName,
  onHoverRoute,
  onFlyToStep,
  onHoverElevation,
  geometry,
}) => {
  const [showLegs, setShowLegs] = useState(false);
  const [showAlternatives, setShowAlternatives] = useState(true);

  const data = section.data;
  if (!data) {
    return (
      <Box>
        <TabHeader title="Route" help={<DockTabHelp tab="line" />} />
        {section.loading
          ? <Loading label="Asking Mapbox for routes…" />
          : (
            <EmptyState>
              Drop an origin and a destination on the map. Turn-by-turn directions,
              alternatives and the elevation profile appear here and stay put while
              you work.
            </EmptyState>
          )}
      </Box>
    );
  }

  const legs = data.legs || [];
  const alternatives = data.alternatives || [];
  const steps = legs.flatMap((leg) => leg.steps || []);
  const trafficDelay = data.durationTypical != null ? data.duration - data.durationTypical : null;
  const arrival = formatArrival(data.departAt, data.duration);
  const primary = alternatives.find((a) => a.primary) || null;
  // Held content is dimmed while a new route is on the way, so an operator can
  // keep reading the old directions without mistaking them for the new ones.
  const dim = section.loading || section.stale;

  return (
    <Box>
      <TabHeader
        title="Route"
        hint={section.loading ? 'recalculating…' : undefined}
        help={<DockTabHelp tab="line" />}
      />

      <Box sx={{ opacity: dim ? 0.55 : 1, transition: 'opacity 120ms' }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, mb: 0.25 }}>
          <Typography variant="body2" sx={{ fontWeight: 800 }}>
            {formatDuration(data.duration)}
          </Typography>
          <Typography variant="body2">{formatDistance(data.distance, units)}</Typography>
          {arrival && (
            <Typography variant="caption" color="text.secondary">arrive {arrival}</Typography>
          )}
        </Box>
        {trafficDelay != null && Math.abs(trafficDelay) > 60 && (
          <Typography
            variant="caption"
            sx={{ display: 'block', mb: 0.5, color: trafficDelay > 0 ? '#e0563d' : '#2e9e5b' }}
          >
            {trafficDelay > 0
              ? `${Math.round(trafficDelay / 60)} min slower than free-flow`
              : `${Math.round(Math.abs(trafficDelay) / 60)} min faster than typical`}
          </Typography>
        )}

        {alternatives.length > 1 && (
          <>
            <SubHeader
              label="Named routes"
              hint={`${alternatives.length - 1} other${alternatives.length === 2 ? '' : 's'}`}
              open={showAlternatives}
              onToggle={() => setShowAlternatives((v) => !v)}
            />
            <Collapse in={showAlternatives}>
              {alternatives.map((alt, index) => (
                <Box
                  key={alt.id}
                  onMouseEnter={() => onHoverRoute?.(index)}
                  onMouseLeave={() => onHoverRoute?.(null)}
                  onClick={() => onSelectRoute?.(index)}
                  sx={{
                    display: 'flex', alignItems: 'baseline', gap: 1, py: 0.35, px: 0.5,
                    borderRadius: 0.75,
                    cursor: onSelectRoute ? 'pointer' : 'default',
                    borderLeft: '2px solid',
                    borderColor: alt.primary
                      ? '#4c8bf5'
                      : (index === alternateIndex ? ALTERNATE_COLOR : 'transparent'),
                    '&:hover': onSelectRoute ? { bgcolor: 'action.hover' } : undefined,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: alt.primary ? 800 : 600, minWidth: 54 }}>
                    {formatDuration(alt.duration)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 52 }}>
                    {formatDistance(alt.distance, units)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
                    {alt.primary
                      ? (primaryName?.trim() || alt.summary || 'Primary')
                      : (index === alternateIndex && alternateName?.trim())
                        ? alternateName
                        : formatDelta(
                          alt.duration - (primary?.duration ?? alt.duration),
                          alt.distance - (primary?.distance ?? alt.distance),
                          units,
                        )}
                  </Typography>
                  {onSetAlternate && !alt.primary && (
                    <Tooltip title={index === alternateIndex
                      ? 'Stop keeping this as the fallback'
                      : 'Keep this as the mission fallback'}
                    >
                      <Typography
                        component="span"
                        variant="caption"
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); onSetAlternate(index); }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.stopPropagation(); onSetAlternate(index); }
                        }}
                        sx={{
                          cursor: 'pointer',
                          fontWeight: 800,
                          fontSize: '0.62rem',
                          letterSpacing: 0.2,
                          px: 0.6,
                          py: 0.15,
                          borderRadius: 0.75,
                          border: '1px solid',
                          borderColor: index === alternateIndex ? ALTERNATE_COLOR : '#333a45',
                          color: index === alternateIndex ? ALTERNATE_COLOR : '#c6cdd5',
                          '&:hover': { color: ALTERNATE_COLOR, borderColor: ALTERNATE_COLOR },
                        }}
                      >
                        {index === alternateIndex ? 'Fallback' : 'Use as fallback'}
                      </Typography>
                    </Tooltip>
                  )}
                </Box>
              ))}
            </Collapse>
          </>
        )}

        {legs.length > 1 && (
          <>
            <SubHeader
              label="Legs"
              hint={`${legs.length}`}
              open={showLegs}
              onToggle={() => setShowLegs((v) => !v)}
            />
            <Collapse in={showLegs}>
              {legs.map((leg, index) => (
                <Box key={`leg-${index}`} sx={{ display: 'flex', alignItems: 'baseline', gap: 1, py: 0.25 }}>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
                    {(data.waypointLabels || [])[index] || `Stop ${index + 1}`}
                    {' → '}
                    {(data.waypointLabels || [])[index + 1] || `Stop ${index + 2}`}
                  </Typography>
                  <Typography variant="caption">{formatDistance(leg.distance, units)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDuration(leg.duration)}
                  </Typography>
                </Box>
              ))}
            </Collapse>
          </>
        )}

        {geometry && (
          <ElevationProfile geometry={geometry} units={units} onHoverPoint={onHoverElevation} />
        )}

        <Box sx={{ mt: 0.75, mb: 0.25 }}>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            Directions
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
            {steps.length} steps
          </Typography>
        </Box>
        {steps.map((step, index) => (
          <Box
            key={`step-${index}`}
            onClick={() => onFlyToStep?.(step)}
            sx={{
              display: 'flex', alignItems: 'flex-start', gap: 0.75, py: 0.3, px: 0.5,
              borderRadius: 0.75,
              cursor: onFlyToStep ? 'pointer' : 'default',
              '&:hover': onFlyToStep ? { bgcolor: 'action.hover' } : undefined,
            }}
          >
            <Box sx={{ pt: 0.15 }}>{maneuverIcon(step)}</Box>
            <Typography variant="caption" sx={{ flex: 1, lineHeight: 1.35 }}>
              {step.instruction || step.name || 'Continue'}
            </Typography>
            {step.distance > 0 && (
              <Typography variant="caption" color="text.secondary">
                {formatDistance(step.distance, units)}
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      <Provenance section={section} />
    </Box>
  );
};

export default DirectionsTab;
